import { Component, DoCheck, ElementRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { TsInputComponent } from 'tsui';

import UrlUtils from '@shared/Utils/url.utils';
import { FormFieldComponent } from '../form-field.types';
import { FormatNumberPipe } from '@shared/pipes/format-number.pipe';

@Component({
  templateUrl: './decimal-form-field.component.html',
  styleUrls: ['./../form-field.component.scss'],
})
export class DecimalFormFieldComponent extends FormFieldComponent implements DoCheck {
  @ViewChild(TsInputComponent) private readonly tsInput?: TsInputComponent;

  private _originalValue: any;

  public value: any;
  public editing: boolean = false;
  public precision: number = 0;
  public readonly control = new FormControl<number | string | null>(null);
  // Hidden values are never editable, so they get their own control: keeping 'REDACTED' out of
  // `control` stops the placeholder from reaching the save payload.
  public readonly redactedControl = new FormControl({ value: 'REDACTED', disabled: true });
  // Overrides the inherited Material-specific selector: ts-input has no .mat-form-field-flex to target.
  public readonly commonTargetStylingClass = '.ts-input';

  constructor(private _formatNumber: FormatNumberPipe) {
    super();
  }

  // Duck-typed contract read by form-field.component.ts to avoid clobbering an in-progress edit.
  get inputControl(): ElementRef<HTMLInputElement> | undefined {
    return this.tsInput?.inputElement();
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.value = this.model[this.fieldDef.name];

    this._originalValue = this.value;

    if (this.fieldDef.meta && this.fieldDef.meta.scale) {
      this.precision = this.fieldDef.meta.scale;
    }

    //SC-40441, Don't format for the latitude and the longitude, or when the value is hidden
    if (!this.isCoordinateField() && !this.fieldDef.meta?.value_hidden) {
      this.value = this._formatNumber.transform(this.value, this.precision, this.fieldDef);
    }

    this.syncDisplay();
    this.updateDisabledState();

    this.control.valueChanges.pipe(takeUntilDestroyed(this.dRef)).subscribe((value) => {
      this.value = value;
    });
  }

  ngDoCheck(): void {
    this.updateDisabledState();
  }

  updateChangedModel(model: any): void {
    this.model = model;
    this.value = this.model[this.fieldDef.name];
    this._originalValue = this.value;

    this.syncDisplay();
  }

  edit(): void {
    if (!this.editing && !this.isReadonly && !this.fieldDef.meta?.value_hidden) {
      this.editing = true;
      this.syncDisplay();
      setTimeout(() => {
        this.inputControl?.nativeElement.focus();
      }, 300);
    }
  }

  onBlur(): void {
    this.value = UrlUtils.checkAndEvalFormula(this.value)?.toString();

    if (this.value === '') {
      this.value = null;
    }

    this.onSave();
    this.editing = false;
    this.syncDisplay();
  }

  private onSave(): void {
    if (this._originalValue != this.value) {
      this.updateModel();
      this.update.emit({ [this.fieldDef.name]: this.value ? this.value.replace(/,/g, '') : null });
    }
  }

  private updateModel(): void {
    this.model[this.fieldDef.name] = this._formatNumber.transform(this.value, this.precision, this.fieldDef);
    this._originalValue = this._formatNumber.transform(this.value, this.precision, this.fieldDef);

    //SC-40441, Don't format for the latitude and the longitude
    if (this.isCoordinateField()) {
      return;
    }

    this.value = this._formatNumber.transform(this.value, this.precision, this.fieldDef);
  }

  // Shows the value as-is while editing and for coordinates (SC-40441), formatted otherwise. Writes without
  // emitting so the formatted text never replaces `value`.
  private syncDisplay(): void {
    const displayValue =
      this.editing || this.isCoordinateField()
        ? this.value
        : this._formatNumber.transform(this.value, this.precision, this.fieldDef);

    this.control.setValue(displayValue, { emitEvent: false });
  }

  private isCoordinateField(): boolean {
    return this.fieldDef.name === 'latitude' || this.fieldDef.name === 'longitude';
  }

  private updateDisabledState(): void {
    if (this.isDisabled === this.control.disabled) {
      return;
    }

    if (this.isDisabled) {
      this.control.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });
    }
  }
}
